export function hashStr(str: string): number {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = (h * 16777619) >>> 0;
    }
    return h;
}

export function mulberry32(seed: number): () => number {
    return function () {
        seed |= 0;
        seed = (seed + 0x6d2b79f5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export interface AvatarVars {
    '--c1': string;
    '--c2': string;
    '--c3': string;
    '--c4': string;
    '--c5': string;
}

export function getAvatarVars(seed: string | number): AvatarVars {
    const rnd = mulberry32(hashStr(String(seed)));
    const h1 = Math.floor(rnd() * 360);
    const h2 = (h1 + 40 + Math.floor(rnd() * 90)) % 360;
    const h3 = (h1 + 180 + Math.floor(rnd() * 60)) % 360;
    const s = 70 + Math.floor(rnd() * 20);
    const l1 = 55 + Math.floor(rnd() * 12);
    const l2 = 35 + Math.floor(rnd() * 18);
    return {
        '--c1': `hsl(${h1} ${s}% ${l1}%)`,
        '--c2': `hsl(${h2} ${s}% ${l1}%)`,
        '--c3': `hsl(${h3} ${s}% ${l1 + 10}%)`,
        '--c4': `hsl(${h1} ${Math.max(20, s - 30)}% ${l2}%)`,
        '--c5': `hsl(${(h1 + 200) % 360} 18% 14%)`,
    };
}

const segmentVars = Array.from({ length: 16 }, (_, i) => `var(--u${i + 1})`);

export function segmentColor(index: number): string {
    return segmentVars[((index % 16) + 16) % 16];
}

export function avatarPrimary(seed: string | number): string {
    const rnd = mulberry32(hashStr(String(seed)));
    const h = Math.floor(rnd() * 360);
    return `hsl(${h} 75% 60%)`;
}

/**
 * Normalize a Steam avatar URL to the high-res variant (_full.jpg, 184×184)
 * served from the reliable Cloudflare CDN.
 *
 * Steam serves three sizes for each avatar:
 *   - `<hash>.jpg`        →  32×32  (default / "small")
 *   - `<hash>_medium.jpg` →  64×64
 *   - `<hash>_full.jpg`   → 184×184
 *
 * Two things go wrong with backend-saved avatars:
 *   1. The medium / small variant is saved. Rendered at 60–80px those look
 *      blurry / "small" because Next/Image can't upscale past the source.
 *   2. The bare `avatars.steamstatic.com` (and akamai/fastly mirror) hosts are
 *      unreliable — they sometimes fail to load, leaving an empty block. The
 *      `avatars.cloudflare.steamstatic.com` mirror serves identical content
 *      reliably, so we rewrite onto it.
 *
 * Returns the URL unchanged for non-Steam CDN hosts.
 */
export function toFullSteamAvatar(url: string): string;
export function toFullSteamAvatar(url: string | null | undefined): string | undefined;
export function toFullSteamAvatar(url: string | null | undefined): string | undefined {
    if (!url) return undefined;
    const isSteam =
        url.includes('steamstatic.com') || url.includes('steamcdn-a.akamaihd.net');
    if (!isSteam) return url;

    // Prefer the reliable Cloudflare mirror over the bare / akamai / fastly
    // `*.steamstatic.com` avatar hosts, which can fail to load.
    let out = url.replace(
        /\/\/avatars(?:\.(?:akamai|fastly))?\.steamstatic\.com\//i,
        '//avatars.cloudflare.steamstatic.com/'
    );

    // Already _full
    if (/_full\.(jpe?g|png|gif)(\?|$)/i.test(out)) return out;
    // _medium → _full
    if (/_medium\.(jpe?g|png|gif)(\?|$)/i.test(out)) {
        return out.replace(/_medium(\.(?:jpe?g|png|gif))/i, '_full$1');
    }
    // Bare <hash>.jpg → <hash>_full.jpg (only when the basename has no underscore suffix)
    const m = out.match(/\/([a-f0-9]{20,64})(\.(?:jpe?g|png|gif))(\?|$)/i);
    if (m) {
        return out.replace(`/${m[1]}${m[2]}`, `/${m[1]}_full${m[2]}`);
    }
    return out;
}
